import React, { useState, useCallback, useRef, useMemo, useSyncExternalStore, useEffect, useLayoutEffect, useReducer } from 'react';
import { flushSync } from 'react-dom';
import { Layer, Group, Circle, Rect, Text, Line, Image as KonvaImage } from 'react-konva';
import { CELL_SIZE, snapToGrid, gridToPixel } from './GridLayer';
import { useCampaign } from '../../contexts/CampaignContext';
import { useMap } from '../../contexts/MapContext';
import { useAuth } from '../../contexts/AuthContext';
import { currentWeight, encumbranceTier, speedPenalty } from '../../lib/inventory';
import { BUFF_MAP } from '../../lib/buffs';
import { OBJECT_MAP, objectFootprint } from '../../lib/objects';
import { renderSprite, renderObjectSprite } from '../../lib/sprites';
import { buildWallSegs, buildDoorVisionSegs, computeVisibleCells } from '../../lib/vision';
import { edgeKey, buildWallEdges, buildWallBlockedCells, wallBlocksStep, computeReachable, findPath } from '../../lib/pathfinding';
import { passRule, isProne as tokenIsProne } from '../../lib/creatureSpace';
import * as tokenAnimator from '../../lib/tokenAnimator';
import * as paperdollRenderer from '../../lib/paperdollRenderer';
import * as monsterPackRenderer from '../../lib/monsterPackRenderer';
import { monsterPackKey } from '../../lib/monsterPackMap';
import { MONSTER_PACKS } from '../../lib/monsterPacks';
import { findTeleportFromCell } from '../../lib/teleports';
import { canAppendCell, routeTotalCost } from '../../lib/routeDrawing';

// ── Helpers ────────────────────────────────────────────────────────────────

function hpConditionColor(hp, maxHp) {
  if (hp <= 0)           return '#4b5563';
  const pct = hp / maxHp;
  if (pct > 0.5)         return '#4ade80';
  if (pct > 0.35)        return '#fb923c';
  if (pct > 0.15)        return '#facc15';
  return '#f87171';
}

const COND_COLORS = {
  blinded: '#888888', charmed: '#ff69b4', deafened: '#aaaaaa',
  exhaustion: '#888800', frightened: '#884400', grappled: '#884400',
  incapacitated: '#ff8800', invisible: '#88aaff', paralyzed: '#ffff00',
  petrified: '#aaaaaa', poisoned: '#00cc44', prone: '#aaaaaa',
  restrained: '#cc8800', stunned: '#ff8800', unconscious: '#4444cc',
  burrowed: '#a0784a',
};

function abbreviate(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return name.slice(0, 6);
  return parts.map((p) => p[0]).join('').toUpperCase().slice(0, 4);
}

// Wall edge / pathfinding utilities are in ../../lib/pathfinding.js


// ── Door edge / blocked-cell computation ──────────────────────────────────
// Doors are single-segment walls that only block when not open.
function buildDoorEdges(doors) {
  if (!doors || doors.length === 0) return new Set();
  // Convert non-open doors into a wall-like format and reuse buildWallEdges
  const fakeDoors = doors
    .filter((d) => d.state !== 'open')
    .map((d) => ({ pts: d.pts, closed: false }));
  return buildWallEdges(fakeDoors);
}

function buildDoorBlockedCells(doors) {
  if (!doors || doors.length === 0) return new Set();
  const fakeDoors = doors
    .filter((d) => d.state !== 'open')
    .map((d) => ({ pts: d.pts, closed: false }));
  return buildWallBlockedCells(fakeDoors);
}

// ── BFS movement range ─────────────────────────────────────────────────────
// Returns the set of grid coords reachable from (startX,startY) within speedCells
// steps, respecting impassable terrain, enemy-occupied cells, and wall edges.
// Pass speedCells=9999 for unrestricted-distance moves (DM outside combat) —
// terrain and walls still block, only the distance cap is removed.

// Check if all cells a token occupies at (gx, gy) are passable and non-empty
function isDestinationValid(gx, gy, tokenSize, cells, gridW, gridH) {
  for (let dx = 0; dx < tokenSize; dx++) {
    for (let dy = 0; dy < tokenSize; dy++) {
      if (gx + dx >= gridW || gy + dy >= gridH) return false;
      const idx  = (gy + dy) * gridW + (gx + dx);
      const cell = cells[idx];
      if (!cell || cell.terrain === 'empty' || !cell.passable) return false;
    }
  }
  return true;
}

// ── Route overlay ──────────────────────────────────────────────────────────
// Player long-press route-drawing visualisation. Renders tinted cells the
// player has drawn through, a polyline connecting them, a pulsing head, and a
// remaining-budget badge. Lives inside TokenLayer's Layer.
function RouteOverlay({ drawing }) {
  if (!drawing) return null;
  const { route, anchor, remainingCells, footprint } = drawing;
  const w = footprint || 1;
  const cellW = CELL_SIZE * w;

  // Polyline points: anchor center → each route cell center
  const pts = [];
  pts.push((anchor.x + w / 2) * CELL_SIZE, (anchor.y + w / 2) * CELL_SIZE);
  for (const c of route) {
    pts.push((c.x + w / 2) * CELL_SIZE, (c.y + w / 2) * CELL_SIZE);
  }

  const head = route.length > 0 ? route[route.length - 1] : anchor;
  const exhausted = remainingCells <= 0;

  return (
    <Group listening={false}>
      {route.map((c, i) => (
        <Rect
          key={`r${i}`}
          x={c.x * CELL_SIZE}
          y={c.y * CELL_SIZE}
          width={cellW}
          height={cellW}
          fill={c.difficult ? 'rgba(245,158,11,0.30)' : 'rgba(56,189,248,0.28)'}
          stroke={c.difficult ? 'rgba(245,158,11,0.7)' : 'rgba(56,189,248,0.7)'}
          strokeWidth={1}
          listening={false}
          perfectDrawEnabled={false}
        />
      ))}
      {pts.length >= 4 && (
        <Line
          points={pts}
          stroke="#38bdf8"
          strokeWidth={4}
          opacity={0.85}
          lineCap="round"
          lineJoin="round"
          shadowColor="#38bdf8"
          shadowBlur={8}
          shadowEnabled
          listening={false}
          perfectDrawEnabled={false}
        />
      )}
      {/* Pulsing head */}
      <Circle
        x={(head.x + w / 2) * CELL_SIZE}
        y={(head.y + w / 2) * CELL_SIZE}
        radius={8}
        fill="#38bdf8"
        opacity={0.85}
        listening={false}
      />
      {/* Remaining-budget badge */}
      <Group x={(head.x + w / 2) * CELL_SIZE + 14} y={(head.y + w / 2) * CELL_SIZE - 14}>
        <Rect
          x={0}
          y={-10}
          width={88}
          height={20}
          cornerRadius={4}
          fill="rgba(8,12,28,0.85)"
          stroke={exhausted ? '#ef4444' : '#38bdf8'}
          strokeWidth={1}
          listening={false}
        />
        <Text
          x={0}
          y={-7}
          width={88}
          align="center"
          text={exhausted ? 'no movement' : `${remainingCells * 5} ft left`}
          fontSize={11}
          fontStyle="bold"
          fill={exhausted ? '#ef4444' : '#7dd3fc'}
          listening={false}
        />
      </Group>
    </Group>
  );
}

// ── Move range highlight layer ─────────────────────────────────────────────
function MoveRangeLayer({ reachableCells, difficultCells }) {
  if (!reachableCells || reachableCells.size === 0) return null;

  const rects = [];
  for (const key of reachableCells) {
    const [x, y] = key.split(',').map(Number);
    const isDifficult = !!(difficultCells && difficultCells.has(key));
    rects.push(
      <Rect
        key={key}
        x={x * CELL_SIZE} y={y * CELL_SIZE}
        width={CELL_SIZE} height={CELL_SIZE}
        fill={isDifficult ? 'rgba(245,158,11,0.22)' : 'rgba(74,222,128,0.15)'}
        stroke={isDifficult ? 'rgba(245,158,11,0.6)' : 'rgba(74,222,128,0.4)'}
        strokeWidth={0.8}
        listening={false}
      />
    );
  }
  return <Layer listening={false}>{rects}</Layer>;
}

// ── Object token ───────────────────────────────────────────────────────────
function ObjectToken({ token, cellPxW, cellPxH, hpPct, condColour, selected, isActiveToken, isDm, ghostMode }) {
  const objDef      = OBJECT_MAP[token.objectType] || {};
  const isDestroyed = token.hp <= 0;
  const colour      = token.colour || objDef.color || '#5a3a18';
  const isRect      = cellPxW !== cellPxH;
  const { baseW, baseH } = isRect ? objectFootprint(token) : { baseW: 1, baseH: 1 };
  const spriteCellW = CELL_SIZE;
  const spriteCellH = CELL_SIZE * baseH;
  const sprite      = renderObjectSprite(token.objectType, colour, spriteCellW, baseH);
  const rotation    = token.rotation || 0;

  const cx = cellPxW / 2, cy = cellPxH / 2;
  const barW = cellPxW - 6, barH = 4, barX = 3, barY = cellPxH - barH - 2;
  const ghostOpacity = ghostMode ? 0.38 : 1;

  return (
    <>
      <Rect x={0} y={0} width={cellPxW} height={cellPxH} fill="rgba(0,0,0,0.01)" />
      {selected && (
        <Rect x={-4} y={-4} width={cellPxW + 8} height={cellPxH + 8}
          stroke="#fff" strokeWidth={1.5} fill="transparent" dash={[5,4]} cornerRadius={4} listening={false} />
      )}
      {isActiveToken && (
        <>
          <Rect x={-4} y={-4} width={cellPxW + 8} height={cellPxH + 8}
            stroke="#facc15" strokeWidth={2} fill="transparent" opacity={0.4} cornerRadius={4} listening={false} />
          <Rect x={-2} y={-2} width={cellPxW + 4} height={cellPxH + 4}
            stroke="#facc15" strokeWidth={4} fill="transparent"
            shadowColor="#facc15" shadowBlur={15} shadowEnabled cornerRadius={4} listening={false} />
        </>
      )}
      {sprite ? (
        isRect ? (
          <KonvaImage
            image={sprite}
            x={cx} y={cy}
            offsetX={spriteCellW / 2} offsetY={spriteCellH / 2}
            width={spriteCellW - 4} height={spriteCellH - 4}
            rotation={rotation}
            opacity={isDestroyed ? 0.35 : ghostOpacity} listening={false}
          />
        ) : (
          <KonvaImage image={sprite} x={2} y={2} width={cellPxW - 4} height={cellPxH - 4}
            opacity={isDestroyed ? 0.35 : ghostOpacity} listening={false} />
        )
      ) : (
        <Rect x={2} y={2} width={cellPxW - 4} height={cellPxH - 4} cornerRadius={4}
          fill={isDestroyed ? '#2a2a2a' : colour} opacity={ghostOpacity} listening={false} />
      )}
      {isDestroyed && (
        <Text x={0} y={cy - 10} width={cellPxW} text="💀"
          fontSize={18} align="center" listening={false} />
      )}
      {isDm && <>
        <Rect x={barX} y={barY} width={barW} height={barH} fill="#000" opacity={0.5} cornerRadius={2} listening={false} />
        {hpPct > 0 && <Rect x={barX} y={barY} width={barW * hpPct} height={barH} fill={condColour} cornerRadius={2} listening={false} />}
      </>}
    </>
  );
}

// ── Player token (8-bit sprite) ────────────────────────────────────────────
// Battle-anim pacing — only plays while the token is `engaged`, not every
// player every round. ~150ms/frame × 6 frames ≈ 900ms cycle (slightly slower
// than walking, faster than idle).
const BATTLE_MS_PER_FRAME = 150;
const ATTACK_FRAMES       = 6;

function PlayerToken({ token, cellPx, hpPct, condColour, ringColour, selected, isActiveToken, ghostMode, combatAnim }) {
  const paperdoll = token.paperdoll || null;
  const hasPaperdoll = !!paperdoll;
  const pdSize = Math.round(cellPx * 2.6);
  const liveAnim = hasPaperdoll ? tokenAnimator.getAnimFrame(token.id, token.facing) : null;
  // While in combat, replace the idle pose with the player's chosen attack
  // cycle (Mel/Ran/Mag). Move animations still take precedence — walking plays
  // the walk cycle, not the attack cycle.
  const animInfo = (liveAnim && combatAnim && liveAnim.anim === 'Idl')
    ? {
        anim: combatAnim,
        dir:  liveAnim.dir,
        frame: Math.floor(performance.now() / BATTLE_MS_PER_FRAME) % ATTACK_FRAMES,
      }
    : liveAnim;
  const pdSprite = hasPaperdoll
    ? paperdollRenderer.getPaperdollFrame(paperdoll, animInfo.anim, animInfo.dir, animInfo.frame, pdSize)
    : null;
  const sprite   = hasPaperdoll ? null : renderSprite(token.class, token.colour || '#c084fc', cellPx);
  const radius   = cellPx / 2 - 2;
  const barW = cellPx - 6, barH = 4, barX = 3, barY = cellPx - barH - 2;
  const conditions = token.conditions || [];
  const buffs      = (token.buffs || []).filter((b) => b.id !== 'mirror_image');
  const ghostOpacity = ghostMode ? 0.38 : 1;

  return (
    <>
      {/* Transparent hit area — required for drag events on the Group */}
      <Circle x={cellPx/2} y={cellPx/2} radius={cellPx/2} fill="rgba(0,0,0,0.01)" />
      {selected && (
        <Circle x={cellPx/2} y={cellPx/2} radius={radius + 9}
          stroke="#fff" strokeWidth={1.5} fill="transparent" dash={[5,4]} listening={false} />
      )}
      {/* Active glow — prominent pulsing ring */}
      {isActiveToken && (
        <>
          <Circle x={cellPx/2} y={cellPx/2} radius={radius + 12}
            stroke="#facc15" strokeWidth={3} fill="transparent" opacity={0.25}
            shadowColor="#facc15" shadowBlur={20} shadowEnabled
            listening={false} />
          <Circle x={cellPx/2} y={cellPx/2} radius={radius + 8}
            stroke="#facc15" strokeWidth={2.5} fill="transparent" opacity={0.5}
            shadowColor="#facc15" shadowBlur={12} shadowEnabled
            listening={false} />
        </>
      )}
      {/* Coloured ring */}
      <Circle x={cellPx/2} y={cellPx/2} radius={radius + 4}
        stroke={ringColour} strokeWidth={isActiveToken ? 5 : 1.5}
        fill={token.colour || '#c084fc'} opacity={ghostMode ? 0.1 : 0.25}
        shadowColor={isActiveToken ? '#facc15' : undefined} shadowBlur={isActiveToken ? 15 : 0} shadowEnabled={isActiveToken}
        listening={false} />
      {/* Sprite */}
      {hasPaperdoll ? (
        pdSprite ? (
          <KonvaImage
            image={pdSprite}
            x={(cellPx - pdSize) / 2}
            // Anchor sprite feet (row 85 of the 128 frame) to a fixed
            // fraction of cell height so every token plants at the same
            // baseline regardless of pdSize or per-character height scale.
            // The +heightScale offset compensates for actual feet rows
            // sitting slightly above row 85 in the source PNGs — without it
            // taller (heightScale>1) characters appear to float above the
            // baseline relative to shorter ones.
            y={cellPx * 0.92 - pdSize * (85 / 128) + (((paperdoll.heightScale ?? 1) > 1 ? ((paperdoll.heightScale ?? 1) - 1) * 0.18 : (1 - (paperdoll.heightScale ?? 1)) * 0.14)) * pdSize}
            width={pdSize}
            height={pdSize}
            opacity={ghostOpacity}
            listening={false}
          />
        ) : (
          <Circle x={cellPx/2} y={cellPx/2} radius={radius} fill={token.colour || '#c084fc'} opacity={0.4} />
        )
      ) : sprite ? (
        <KonvaImage image={sprite} x={2} y={2} width={cellPx - 4} height={cellPx - 4} opacity={ghostOpacity} listening={false} />
      ) : (
        <Circle x={cellPx/2} y={cellPx/2} radius={radius} fill={token.colour || '#c084fc'} opacity={ghostMode ? 0.38 : 0.88} />
      )}
      {/* HP bar */}
      <Rect x={barX} y={barY} width={barW} height={barH} fill="#000" opacity={0.5} cornerRadius={2} listening={false} />
      <Rect x={barX} y={barY} width={barW * hpPct} height={barH} fill={condColour} cornerRadius={2} listening={false} />
      {(token.tempHp || 0) > 0 && (
        <Rect x={barX} y={barY - barH - 1}
          width={Math.min(barW, barW * (token.tempHp / (token.maxHp || 10)))}
          height={barH} fill="#60a5fa" cornerRadius={2} opacity={0.7} listening={false} />
      )}
      {conditions.slice(0, 4).map((cond, i) => (
        <Circle key={`c${cond}`} x={barX + 5 + i * 8} y={barY - 8} radius={3}
          fill={COND_COLORS[cond] || '#888'} listening={false} shadowColor="#000" shadowBlur={2} />
      ))}
      {buffs.slice(0, 4).map((buff, i) => {
        const def = BUFF_MAP[buff.id];
        return <React.Fragment key={`b${i}`}>
          <Circle x={barX + 5 + i * 8} y={barY - 16} radius={3}
            fill={def?.color || '#c084fc'} listening={false} shadowColor="#000" shadowBlur={2} />
          {buff.charges !== undefined && buff.charges > 0 && (
            <Text x={barX + 5 + i * 8 + 4} y={barY - 20} text={`${buff.charges}`}
              fontSize={8} fontStyle="bold" fill={def?.color || '#818cf8'} listening={false} />
          )}
        </React.Fragment>;
      })}
    </>
  );
}

// ── Creature token ─────────────────────────────────────────────────────────
function CreatureToken({ token, cellPx, hpPct, condColour, ringColour, selected, isActiveToken, isDm, ghostMode, packKey }) {
  const radius     = (cellPx / 2) - 3;
  const conditions = token.conditions || [];
  const buffs      = (token.buffs || []).filter((b) => b.id !== 'mirror_image');
  const visibleConds = conditions.slice(0, 4);
  const visibleBuffs = buffs.slice(0, 4);
  const barW = cellPx - 6, barH = 4, barX = 3, barY = cellPx - barH - 2;
  const showHpBar  = isDm || token.type !== 'monster';
  const ghostOpacity = ghostMode ? 0.38 : 1;
  // At 0 HP: dim sprite + show skull. State is reversible — heal flips it back.
  const isDefeated = (token.hp ?? 1) <= 0;
  const defeatDim  = isDefeated ? 0.55 : 1;

  // Pack sprite takes priority when we can resolve one. Scale proportionally to
  // the pack's native frame size: 128px packs render at 1.8× cellPx (character
  // overhang), while 64px packs (props like fire, pillars) render at 0.9× cellPx
  // to stay within the cell.
  const packFrameSize = packKey ? (MONSTER_PACKS[packKey]?.anims?.idle?.frameSize || MONSTER_PACKS[packKey]?.frameSize || 128) : 128;
  const packScale = (packFrameSize / 128) * 1.8;
  const packSize = Math.round(cellPx * packScale);
  const animInfo = packKey ? tokenAnimator.getAnimFrame(token.id, token.facing) : null;
  const packAnim = animInfo ? (animInfo.anim === 'Mov' ? 'move' : 'idle') : 'idle';
  // 64px single-row idle-only packs (fire, pillars) are variant strips — each
  // cell is a different design, not an animation frame. Show frame 0.
  const packIsVariantStrip = packFrameSize < 128 && (MONSTER_PACKS[packKey]?.anims?.idle?.rows || 4) === 1;
  const packFrame = packIsVariantStrip ? 0 : (animInfo?.frame ?? 0);
  const packSprite = packKey
    ? monsterPackRenderer.getMonsterPackFrame(packKey, packAnim, animInfo?.dir ?? 'down', packFrame, packSize)
    : null;

  return (
    <>
      {/* Transparent hit area — required for drag/click events on the Group */}
      <Circle x={cellPx / 2} y={cellPx / 2} radius={cellPx / 2} fill="rgba(0,0,0,0.01)" />
      {selected && <Circle x={cellPx / 2} y={cellPx / 2} radius={radius + 9} stroke="#fff" strokeWidth={1.5} fill="transparent" dash={[5, 4]} listening={false} />}
      {isActiveToken && (
        <>
          <Circle x={cellPx / 2} y={cellPx / 2} radius={radius + 12} stroke="#facc15" strokeWidth={3} fill="transparent" opacity={0.25} shadowColor="#facc15" shadowBlur={20} shadowEnabled listening={false} />
          <Circle x={cellPx / 2} y={cellPx / 2} radius={radius + 8} stroke="#facc15" strokeWidth={2.5} fill="transparent" opacity={0.5} shadowColor="#facc15" shadowBlur={12} shadowEnabled listening={false} />
        </>
      )}
      <Circle x={cellPx / 2} y={cellPx / 2} radius={radius + 4} stroke={ringColour} strokeWidth={isActiveToken ? 5 : 1.5} fill={token.colour || '#c084fc'} opacity={(ghostMode ? 0.1 : 0.25) * defeatDim} shadowColor={isActiveToken ? '#facc15' : undefined} shadowBlur={isActiveToken ? 15 : 0} shadowEnabled={isActiveToken} listening={false} />
      {packSprite ? (() => {
        const ws = token.packWidthScale ?? 1;
        const hs = token.packHeightScale ?? 1;
        const drawW = packSize * ws;
        const drawH = packSize * hs;
        const baseX = (cellPx - drawW) / 2 + (token.packOffsetX ?? 0) * cellPx;
        const baseY = packFrameSize < 128
          ? (cellPx - drawH) / 2 + (token.packOffsetY ?? 0) * cellPx
          : cellPx * 0.92 - drawH * (85 / 128) + (token.packOffsetY ?? 0) * cellPx;
        return <KonvaImage
          image={packSprite}
          x={baseX} y={baseY}
          width={drawW} height={drawH}
          opacity={ghostOpacity * defeatDim}
          listening={false}
        />;
      })() : (
        <>
          <Circle x={cellPx / 2} y={cellPx / 2} radius={radius} fill={token.colour || '#c084fc'} opacity={(ghostMode ? 0.38 : 0.88) * defeatDim} />
          <Circle x={cellPx / 2} y={cellPx / 2} radius={radius - 5} stroke={condColour} strokeWidth={2.5} fill="transparent" opacity={ghostOpacity * defeatDim} listening={false} />
          <Text x={0} y={cellPx / 2 - 7} width={cellPx} text={abbreviate(token.name)} fontSize={token.size > 1 ? 13 : 10} fontStyle="bold" fill="#fff" align="center" listening={false} shadowColor="#000" shadowBlur={4} opacity={defeatDim} />
        </>
      )}
      {showHpBar ? <>
        <Rect x={barX} y={barY} width={barW} height={barH} fill="#000" opacity={0.5} cornerRadius={2} listening={false} />
        <Rect x={barX} y={barY} width={barW * hpPct} height={barH} fill={condColour} cornerRadius={2} listening={false} />
        {(token.tempHp || 0) > 0 && <Rect x={barX} y={barY - barH - 1} width={Math.min(barW, barW * ((token.tempHp || 0) / (token.maxHp || 10)))} height={barH} fill="#60a5fa" cornerRadius={2} opacity={0.7} listening={false} />}
      </> : (
        /* Health status ring for monsters (player view) */
        <Circle x={cellPx / 2} y={cellPx / 2} radius={radius + 1} stroke={condColour} strokeWidth={3} fill="transparent" opacity={0.85 * defeatDim} listening={false} />
      )}
      {visibleConds.map((cond, i) => <Circle key={`c${cond}`} x={barX + 5 + i * 8} y={barY - 8} radius={3} fill={COND_COLORS[cond] || '#888'} listening={false} shadowColor="#000" shadowBlur={2} />)}
      {visibleBuffs.map((buff, i) => { const def = BUFF_MAP[buff.id]; return <React.Fragment key={`b${i}`}><Circle x={barX + 5 + i * 8} y={barY - 16} radius={3} fill={def?.color || '#c084fc'} listening={false} shadowColor="#000" shadowBlur={2} />{buff.charges !== undefined && buff.charges > 0 && (<Text x={barX + 5 + i * 8 + 4} y={barY - 20} text={`${buff.charges}`} fontSize={8} fontStyle="bold" fill={def?.color || '#818cf8'} listening={false} />)}</React.Fragment>; })}
      {/* Skull overlay — visible above the dimmed sprite when at 0 HP. Reversible: heal restores normal state. */}
      {isDefeated && (
        <Text
          x={0}
          y={cellPx / 2 - Math.min(20, cellPx * 0.32)}
          width={cellPx}
          text="💀"
          fontSize={Math.min(40, cellPx * 0.65)}
          align="center"
          listening={false}
          shadowColor="#000"
          shadowBlur={8}
        />
      )}
    </>
  );
}

// ── Combined token ─────────────────────────────────────────────────────────
const EMPTY_SET = new Set();
function TokenInner({ token, isDm, isActiveToken, onSelect, selected, highlighted, allTokens, cells, gridW, gridH, onDragStateChange, onDrawingChange, readOnly, wallEdges, wallSegs, wallBlockedCells, queueFogReveal, blockSets }) {
  const { moveToken, updateToken, initiative, traps, triggerTrap, players, teleports } = useCampaign();
  const { doors: mapDoors } = useMap();
  const { auth } = useAuth();

  const inCombat      = initiative.turnOrder.length > 0;
  // Battle animation — only plays while the token is in the `engaged` state
  // (toggled via the ⚔ Engage button by the player or the DM). `combatStyle`
  // from CharacterSetup decides which attack cycle (Mel / Ran / Mag).
  const STYLE_TO_ANIM = { melee: 'Mel', ranged: 'Ran', magic: 'Mag' };
  const combatAnim = (token.engaged && token.type === 'player' && token.combatStyle)
    ? (STYLE_TO_ANIM[token.combatStyle] || 'Mel')
    : null;
  const activeTokenId = initiative.turnOrder[initiative.activeIndex] ?? null;
  const isMyToken     = auth?.playerId && token.ownerId === auth.playerId;
  const isObject      = token.type === 'object';

  const isMirrorSibling = isMyToken && token.mirrorDuplicate;
  const isMyActiveToken = isMyToken && token.id === activeTokenId;
  const isMyMirrorGroup = isMyToken && !token.mirrorDuplicate && token.mirrorGroupId &&
    activeTokenId && Object.values(allTokens).some(
      (t) => t.mirrorDuplicate && t.mirrorGroupId === token.mirrorGroupId && t.id === activeTokenId
    );
  const canDragInCombat = isMyActiveToken || isMirrorSibling || isMyMirrorGroup;

  // Per-Token animation subscription: returns the animator's version while this
  // token is animating (position tween OR paperdoll / pack frame clock), else 0.
  const hasPaperdoll = !!token.paperdoll;
  const packKey = !hasPaperdoll && token.type !== 'object'
    ? (token.packKey || monsterPackKey(token))
    : null;
  const hasAnimatedSprite = hasPaperdoll || !!packKey;
  useSyncExternalStore(
    (cb) => {
      const u1 = tokenAnimator.subscribe(cb);
      const u2 = hasAnimatedSprite ? tokenAnimator.subscribeFrame(cb) : null;
      const u3 = hasPaperdoll ? paperdollRenderer.subscribe(cb) : null;
      const u4 = packKey ? monsterPackRenderer.subscribe(cb) : null;
      return () => { u1(); u2 && u2(); u3 && u3(); u4 && u4(); };
    },
    () => {
      if (tokenAnimator.getAnimation(token.id)) return tokenAnimator.getVersion();
      if (tokenAnimator.isTeleporting(token.id)) return tokenAnimator.getVersion();
      if (hasAnimatedSprite) return tokenAnimator.getVersion();
      return 0;
    },
    () => 0,
  );
  const displayPos = tokenAnimator.getDisplayPos(token.id, token.x, token.y);
  const teleportOpacity = tokenAnimator.getTeleportOpacity(token.id, token.x, token.y);
  const rawPixel = gridToPixel(displayPos.x, displayPos.y);
  const onIntersection = token.onIntersection || false;
  const intersectionOffset = onIntersection ? CELL_SIZE / 2 : 0;
  const pixel   = { x: rawPixel.x + intersectionOffset, y: rawPixel.y + intersectionOffset };
  const size    = token.size || 1;
  // Rectangular footprint for objects (e.g. 1×2 stairs, 1×3 table)
  const foot    = isObject ? objectFootprint(token) : { w: size, h: size, baseW: size, baseH: size };
  const effW    = foot.w;
  const effH    = foot.h;
  const cellPx  = CELL_SIZE * size;           // square tokens still use this
  const cellPxW = CELL_SIZE * effW;
  const cellPxH = CELL_SIZE * effH;
  const hpPct   = Math.max(0, Math.min(1, token.hp / (token.maxHp || 10)));
  const condColour = hpConditionColor(token.hp, token.maxHp || 10);
  const ringColour = isActiveToken ? '#facc15' : (token.colour || '#c084fc');
  const rotation   = token.rotation || 0;

  const originRef = useRef({ x: pixel.x + cellPxW / 2, y: pixel.y + cellPxH / 2 });

  const conditions = token.conditions || [];
  const buffs      = token.buffs      || [];

  // ── Speed modifiers from conditions & buffs ──────────────────────────
  const SPEED_ZERO_CONDITIONS = ['grappled', 'restrained', 'paralyzed', 'stunned', 'petrified', 'unconscious'];
  const isImmobilized = SPEED_ZERO_CONDITIONS.some((c) => conditions.includes(c));

  const isProne       = conditions.includes('prone');
  const isHasted      = buffs.some((b) => b.id === 'hasted');
  const isFlying      = buffs.some((b) => b.id === 'fly');

  // Encumbrance speed penalty (5e tiered). Applies BEFORE prone/haste/dash so
  // those modifiers compound off the already-reduced base, matching RAW.
  // Size category factors into the threshold (Large doubles capacity, etc.).
  const ownerPlayer = token.ownerId ? players?.[token.ownerId] : null;
  const strScore    = ownerPlayer?.abilityScores?.str ?? null;
  const ownerSize   = ownerPlayer?.sizeCategory || token.sizeCategory || 'medium';
  const carryWt     = currentWeight(token.inventory || ownerPlayer?.inventory);
  const encTier     = strScore != null ? encumbranceTier(carryWt, strScore, ownerSize) : 'normal';
  const encPenaltyFt = speedPenalty(encTier);
  const baseSpeedFt = Math.max(0, (token.speed || 30) - encPenaltyFt);

  let speedCells = Math.floor(baseSpeedFt / 5);
  if (isImmobilized) speedCells = 0;
  if (isProne)       speedCells = Math.floor(speedCells / 2);
  if (isHasted)      speedCells = speedCells * 2;
  if (token.dashed)  speedCells = speedCells * 2;

  const movementUsed = token.movementUsed || 0;
  const remainingCells = Math.max(0, speedCells - movementUsed);

  const canDrag = readOnly ? false : isDm
    ? true
    : (isMyToken && (!inCombat || (canDragInCombat && remainingCells > 0)) && !isImmobilized);

  // DM gets unlimited distance outside of their active combat turn so they can
  // freely reposition tokens during setup — terrain still blocks.
  const effectiveSpeed = isDm && !(inCombat && token.id === activeTokenId)
    ? 9999
    : remainingCells;

  // Per 5e creature-space rules: parent hoisted blockedCells/difficultCells
  // for every token in one O(N²) pass and passes the slice down via prop, so
  // each Token doesn't redo the work whenever any other token changes.
  const blockedCells   = blockSets?.blocked   || EMPTY_SET;
  const difficultCells = blockSets?.difficult || EMPTY_SET;

  const handleDragStart = useCallback((e) => {
    originRef.current = { x: e.target.x(), y: e.target.y() };
    // Defensive: if any long-press / route state lingered from a prior
    // cycle that wasn't cleaned up (e.g., pointer release outside the
    // window), wipe it before this drag proceeds.
    isDrawingRef.current = false;
    routeRef.current = [];
    if (drawingCleanupRef.current) {
      drawingCleanupRef.current();
      drawingCleanupRef.current = null;
    }
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    // Show range for all player drags; for DM only on their active combat turn.
    const showRange = !isDm || (inCombat && token.id === activeTokenId);
    if (showRange) {
      const moverProne = tokenIsProne(token);
      const { reachable } = computeReachable(
        token.x, token.y, effectiveSpeed, size,
        cells, blockedCells, gridW, gridH,
        wallEdges, wallBlockedCells,
        isFlying, difficultCells, moverProne,
      );

      // Union creature-space difficult + terrain-difficult cells for the overlay.
      // Flying ignores difficult terrain entirely (no amber). Prone makes every
      // cell difficult, so we mark the whole reachable set amber.
      let visualDifficult;
      if (isFlying) {
        visualDifficult = new Set();
      } else if (moverProne) {
        visualDifficult = new Set(reachable);
      } else {
        visualDifficult = new Set(difficultCells);
        for (const key of reachable) {
          const [x, y] = key.split(',').map(Number);
          if (cells[y * gridW + x]?.difficult) visualDifficult.add(key);
        }
      }
      onDragStateChange(token.id, reachable, visualDifficult);
    }
  }, [token, effectiveSpeed, size, cells, isDm, inCombat, activeTokenId, blockedCells, difficultCells, isFlying, gridW, gridH, wallEdges, wallBlockedCells, onDragStateChange]);

  const wouldCollide = useCallback((newGx, newGy) => {
    if (token.linkedTo) return false;
    const newCells = new Set();
    for (let dx = 0; dx < effW; dx++)
      for (let dy = 0; dy < effH; dy++)
        newCells.add(`${newGx + dx},${newGy + dy}`);
    for (const other of allTokens) {
      if (other.id === token.id || other.defeated) continue;
      if (other.id === token.linkedTo || other.linkedTo === token.id) continue;
      const oFoot = other.type === 'object' ? objectFootprint(other) : { w: other.size || 1, h: other.size || 1 };
      for (let dx = 0; dx < oFoot.w; dx++)
        for (let dy = 0; dy < oFoot.h; dy++)
          if (newCells.has(`${other.x + dx},${other.y + dy}`)) return true;
    }
    return false;
  }, [token, effW, effH, allTokens]);

  const handleDragEnd = useCallback(async (e) => {
    e.cancelBubble = true;
    onDragStateChange(null, null);
    const node     = e.target;
    const rawX     = node.x() - intersectionOffset;
    const rawY     = node.y() - intersectionOffset;
    const snappedX = snapToGrid(rawX - cellPxW / 2) + cellPxW / 2 + intersectionOffset;
    const snappedY = snapToGrid(rawY - cellPxH / 2) + cellPxH / 2 + intersectionOffset;
    const newGx    = Math.floor((snappedX - cellPxW / 2 - intersectionOffset) / CELL_SIZE);
    const newGy    = Math.floor((snappedY - cellPxH / 2 - intersectionOffset) / CELL_SIZE);

    const snapBack = () => { node.x(originRef.current.x); node.y(originRef.current.y); };

    // DM bypasses all movement restrictions — can place tokens anywhere on the grid
    if (!isDm) {
      // Flying tokens bypass terrain passability and walls but still need valid grid coords
      if (!isFlying) {
        if (!isDestinationValid(newGx, newGy, size, cells, gridW, gridH)) return snapBack();

        // Block dropping token onto a cell a wall physically passes through
        if (wallBlockedCells) {
          for (let dx = 0; dx < size; dx++)
            for (let dy = 0; dy < size; dy++)
              if (wallBlockedCells.has(`${newGx + dx},${newGy + dy}`)) return snapBack();
        }
      } else {
        // Flying still needs to land within grid bounds
        if (newGx < 0 || newGy < 0 || newGx + size > gridW || newGy + size > gridH) return snapBack();
      }

      const moverProne = tokenIsProne(token);
      const { reachable } = computeReachable(
        token.x, token.y, effectiveSpeed, size,
        cells, blockedCells, gridW, gridH,
        wallEdges, wallBlockedCells,
        isFlying, difficultCells, moverProne,
      );
      if (!reachable.has(`${newGx},${newGy}`)) return snapBack();
      if (wouldCollide(newGx, newGy)) return snapBack();
    }

    const moverProne = tokenIsProne(token);

    // Compute the path once — drives both the smooth-snap animation and the
    // movement-budget deduction. Cost reflects difficult-terrain (2× per cell)
    // so players are charged the right amount.
    const { path, cost: pathCost } = findPath(
      token.x, token.y, newGx, newGy,
      size, cells, blockedCells, gridW, gridH, wallEdges, wallBlockedCells,
      isFlying, difficultCells, moverProne,
    );

    // ── Hidden Discovery: stop at first passive notice OR first trap ────
    // Two truncation reasons. Whichever path-cell comes earlier wins:
    //   1. Passive notice — moving player would enter passive perception
    //      range (2 cells) of an undiscovered hidden thing whose DC ≤ their
    //      passive score. Stop there; the PassivePerceptionWatcher then
    //      writes discoveredBy for them.
    //   2. Trap step — would step on (or adjacent to) an untriggered trap.
    //      Stop on the trap cell; triggerTrap fires the reaction window.
    let pendingTrapTrigger = null;
    let effectivePath  = path;
    let effectiveDestX = newGx;
    let effectiveDestY = newGy;

    if (path && path.length > 0) {
      const PASSIVE_RANGE = 2;
      const isMyMove   = !isDm && auth?.playerId && token.ownerId === auth.playerId;
      const myRecord   = isMyMove ? players?.[auth.playerId] : null;
      const myPassive  = myRecord?.passivePerception ?? null;
      const myId       = auth?.playerId;

      // Build the candidate hidden-things list for passive discovery.
      const passiveCandidates = [];
      if (isMyMove && myPassive != null) {
        for (const t of Object.values(traps || {})) {
          if (!t || t.triggered) continue;
          if (t.discoveryDC == null) continue;
          if (t.discoveredBy?.[myId]) continue;
          if (myPassive < t.discoveryDC) continue;
          passiveCandidates.push({ x: t.x, y: t.y });
        }
        for (const tk of Object.values(allTokens || {})) {
          if (!tk || tk.id === token.id) continue;
          const isStealthed = (tk.buffs || []).some((b) => b.id === 'stealthed');
          if (!isStealthed || tk.stealthDC == null) continue;
          if (tk.discoveredBy?.[myId]) continue;
          if (myPassive < tk.stealthDC) continue;
          passiveCandidates.push({ x: tk.x, y: tk.y });
        }
        for (const d of mapDoors || []) {
          if (!d || d.type !== 'secret' || d.secretRevealed) continue;
          if (d.discoveryDC == null) continue;
          if (myPassive < d.discoveryDC) continue;
          const a = d.pts?.[0];
          const b = d.pts?.[d.pts.length - 1];
          if (!a || !b) continue;
          passiveCandidates.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
        }
        for (const tp of Object.values(teleports || {})) {
          if (!tp.hidden) continue;
          if (tp.discoveryDC == null) continue;
          if (tp.discoveredBy?.[myId]) continue;
          if (myPassive < tp.discoveryDC) continue;
          passiveCandidates.push({ x: tp.a.x, y: tp.a.y });
          passiveCandidates.push({ x: tp.b.x, y: tp.b.y });
        }
      }

      const trapList = Object.values(traps || {});
      let firstPassiveIdx = -1;
      let firstTrapIdx    = -1;
      let firstTrapHit    = null;

      for (let i = 0; i < path.length; i++) {
        const cellX = path[i].x;
        const cellY = path[i].y;

        if (firstPassiveIdx === -1 && passiveCandidates.length > 0) {
          const hit = passiveCandidates.some((h) =>
            Math.max(Math.abs(h.x - cellX), Math.abs(h.y - cellY)) <= PASSIVE_RANGE
          );
          if (hit) firstPassiveIdx = i;
        }

        if (firstTrapIdx === -1 && triggerTrap && trapList.length > 0) {
          const hit = trapList.find((t) => {
            if (t.triggered) return false;
            if (t.triggerType === 'adjacent') {
              return Math.max(Math.abs(t.x - cellX), Math.abs(t.y - cellY)) <= 1;
            }
            return t.x === cellX && t.y === cellY;
          });
          if (hit) { firstTrapIdx = i; firstTrapHit = hit; }
        }

        if (firstPassiveIdx !== -1 || firstTrapIdx !== -1) break;
      }

      // Earlier event wins.
      if (firstPassiveIdx !== -1 && (firstTrapIdx === -1 || firstPassiveIdx <= firstTrapIdx)) {
        effectivePath  = path.slice(0, firstPassiveIdx + 1);
        effectiveDestX = path[firstPassiveIdx].x;
        effectiveDestY = path[firstPassiveIdx].y;
      } else if (firstTrapIdx !== -1) {
        effectivePath  = path.slice(0, firstTrapIdx + 1);
        effectiveDestX = path[firstTrapIdx].x;
        effectiveDestY = path[firstTrapIdx].y;
        pendingTrapTrigger = firstTrapHit.id;
      }
    }

    // Smooth-snap along walkable path for all tokens except objects out of
    // combat. Routes around walls/objects/enemies instead of sliding through
    // them.
    const shouldSmoothSnap =
      !(token.type === 'object' && !inCombat) &&
      (token.x !== effectiveDestX || token.y !== effectiveDestY);
    if (shouldSmoothSnap) {
      // Reset node to origin pixel so there's no one-frame flash at the drop
      // point before the animator takes over.
      node.x(originRef.current.x);
      node.y(originRef.current.y);
      const waypoints = effectivePath && effectivePath.length > 0
        ? [{ x: token.x, y: token.y }, ...effectivePath]
        : [{ x: token.x, y: token.y }, { x: effectiveDestX, y: effectiveDestY }];
      tokenAnimator.startAnimation(token.id, waypoints);
    } else {
      node.x(snappedX);
      node.y(snappedY);
    }

    await moveToken(token.id, effectiveDestX, effectiveDestY);

    // Fire the trap AFTER the move commits. Damage broadcasts via
    // submitTrapReaction (player) or directly in triggerTrap (monster).
    if (pendingTrapTrigger) {
      triggerTrap(pendingTrapTrigger, token.id);
    }

    // ── Teleport check (only if no trap fired this move) ────────────────
    // Single teleport per drag — prevents A↔B bouncing and A→B→C chains.
    // Shift held during drag-end bypasses entirely (DM convention).
    // Hidden trapdoors fire regardless of discovery state — the surprise is
    // the whole point.
    //
    // Visual: vanish at A, hold invisible, reappear at B (handled by
    // tokenAnimator.startTeleport). The Firestore write goes out while the
    // token is mid-fade so the snapshot lands during the hold phase.
    const shiftBypass = !!e.evt?.shiftKey;
    let teleportRefund = 0;
    if (!pendingTrapTrigger && !shiftBypass && teleports) {
      const hit = findTeleportFromCell(teleports, effectiveDestX, effectiveDestY);
      if (hit) {
        const destCell = hit.dest;
        tokenAnimator.startTeleport(
          token.id,
          { x: effectiveDestX, y: effectiveDestY },
          { x: destCell.x, y: destCell.y },
        );
        // Fire the Firestore write near the end of fade-out so the new
        // committed coords land just as the token reappears at the dest.
        setTimeout(() => {
          moveToken(token.id, destCell.x, destCell.y);
        }, 160);
        // Refund the teleport-entry cell (see commitDrawing for rationale).
        teleportRefund = 1;
      }
    }

    // Track movement used this turn (players only, in combat).
    // When a trap interrupts the move, only charge for cells actually traveled
    // (effectivePath length), not the original full intent.
    if (!isDm && inCombat) {
      const effectiveCost = pendingTrapTrigger
        ? (effectivePath?.length || 0)  // 1 cost per cell walked
        : (Number.isFinite(pathCost) && pathCost > 0
            ? pathCost
            : Math.abs(effectiveDestX - token.x) + Math.abs(effectiveDestY - token.y));
      const netCost = Math.max(0, effectiveCost - teleportRefund);
      updateToken(token.id, { movementUsed: movementUsed + netCost });
    }

    // Reveal fog from the token's new position (players and friendly NPCs only, not mirror duplicates)
    if ((token.type === 'player' || token.type === 'friendlyNPC') && !token.mirrorDuplicate) {
      const visible = computeVisibleCells(effectiveDestX, effectiveDestY, size, wallSegs, cells, gridW, gridH, (token.visionRange || 60) / 5);
      if (visible.length > 0) queueFogReveal(visible);
    }
  }, [token, movementUsed, moveToken, updateToken, isDm, inCombat, isFlying, cellPx, size, cells, effectiveSpeed, wouldCollide, onDragStateChange, blockedCells, difficultCells, gridW, gridH, wallEdges, wallSegs, wallBlockedCells, queueFogReveal, intersectionOffset, traps, triggerTrap, players, mapDoors, allTokens, auth?.playerId, teleports]);

  // ── Long-press route drawing ────────────────────────────────────────────
  // Press and hold (~400 ms) on the player's own token to enter route mode.
  // While the timer is pending we keep the long-press alive only if the
  // pointer stays within 12 px of the press position; a fast drag cancels the
  // timer and lets Konva's drag take over (the existing behaviour).
  //
  // Once route mode is entered we cancel Konva's drag, snap the node back to
  // its origin, and attach window listeners that track each cell the finger
  // passes through. canAppendCell enforces walls + footprint + budget. On
  // release we run the same passive/trap/teleport pipeline as handleDragEnd.
  const pressTimerRef    = useRef(null);
  const pressStartRef    = useRef({ x: 0, y: 0 });
  const isDrawingRef     = useRef(false);
  const routeRef         = useRef([]);
  const drawingCleanupRef = useRef(null);
  const drawNodeRef      = useRef(null);
  // Live refs to current token + movementUsed so the long-press window
  // listeners (which capture a snapshot of commitDrawing at startDrawing
  // time) can read the FRESHEST values rather than the closure's stale
  // version. Without this, when the user starts a new long-press before
  // the previous move's Firestore snapshot has arrived, commitDrawing's
  // captured `token` would still be at the OLD position — the walk
  // animation then starts from the old cell, visually teleporting the
  // token back.
  //
  // Updated SYNCHRONOUSLY during render rather than in useEffect: useEffect
  // runs after commit, so a pointerup arriving in the brief window between
  // commit and effect would read the stale ref. Direct assignment during
  // render is a documented React pattern for keeping refs in sync with
  // props/state (https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents).
  const tokenRef = useRef(token);
  const movementUsedRef = useRef(movementUsed);
  tokenRef.current = token;
  movementUsedRef.current = movementUsed;

  // Allow player drag-to-press only when canDrag and this is the local
  // player's own token. DM keeps normal drag.
  // Long-press route drawing is gated off while we work through the
  // stale-position teleport-back issue. Set ROUTE_DRAWING_ENABLED back to
  // true to re-enable. Normal drag is unaffected.
  const ROUTE_DRAWING_ENABLED = false;
  const canRouteDraw = ROUTE_DRAWING_ENABLED && canDrag && isMyToken && !isDm && !isObject;

  const updateDrawing = useCallback((route) => {
    routeRef.current = route;
    const cost = routeTotalCost(route);
    const baseRemaining = inCombat ? Math.max(0, effectiveSpeed - cost) : Math.max(0, 999);
    const liveToken = tokenRef.current;
    onDrawingChange?.({
      tokenId:        liveToken.id,
      anchor:         { x: liveToken.x, y: liveToken.y },
      route,
      remainingCells: baseRemaining,
      footprint:      size,
    });
  }, [onDrawingChange, effectiveSpeed, inCombat, size]);

  const cancelDrawing = useCallback(() => {
    isDrawingRef.current   = false;
    routeRef.current       = [];
    if (drawingCleanupRef.current) {
      drawingCleanupRef.current();
      drawingCleanupRef.current = null;
    }
    if (drawNodeRef.current) {
      const node = drawNodeRef.current;
      try { node.stopDrag(); } catch { /* node may be gone */ }
      try {
        node.draggable(false);
        setTimeout(() => {
          try { node.draggable(true); } catch { /* node may have unmounted */ }
        }, 0);
      } catch { /* ignore */ }
      drawNodeRef.current = null;
    }
    onDrawingChange?.(null);
    onDragStateChange?.(null, null);
  }, [onDrawingChange, onDragStateChange]);

  const commitDrawing = useCallback(async () => {
    const route = routeRef.current;
    isDrawingRef.current = false;
    if (drawingCleanupRef.current) {
      drawingCleanupRef.current();
      drawingCleanupRef.current = null;
    }
    if (drawNodeRef.current) {
      const node = drawNodeRef.current;
      try { node.stopDrag(); } catch { /* node may be gone */ }
      // Direct Konva attribute toggle, bypassing React batching. Forces
      // Konva to fully release any internal drag state from the long-press
      // hijack so the next pointerdown opens a clean drag.
      try {
        node.draggable(false);
        setTimeout(() => {
          try { node.draggable(true); } catch { /* node may have unmounted */ }
        }, 0);
      } catch { /* ignore */ }
      drawNodeRef.current = null;
    }
    onDrawingChange?.(null);
    onDragStateChange?.(null, null);

    if (!route || route.length === 0) {
      routeRef.current = [];
      return;
    }

    // Build the path as a cell list (excludes the anchor, matches findPath).
    const path = route.map((c) => ({ x: c.x, y: c.y }));

    // Passive notice halt: stop just before any cell within 2 of an
    // undiscovered hidden thing the player would notice.
    let firstPassiveIdx = -1;
    const myId       = auth?.playerId;
    const myRecord   = !isDm && myId ? players?.[myId] : null;
    const myPassive  = myRecord?.passivePerception ?? null;
    const PASSIVE_RANGE = 2;
    const passiveCandidates = [];
    if (myPassive != null) {
      for (const t of Object.values(traps || {})) {
        if (!t || t.triggered) continue;
        if (t.discoveryDC == null) continue;
        if (t.discoveredBy?.[myId]) continue;
        if (myPassive < t.discoveryDC) continue;
        passiveCandidates.push({ x: t.x, y: t.y });
      }
      for (const tk of Object.values(allTokens || {})) {
        if (!tk || tk.id === tokenRef.current.id) continue;
        const isStealthedTk = (tk.buffs || []).some((b) => b.id === 'stealthed');
        if (!isStealthedTk || tk.stealthDC == null) continue;
        if (tk.discoveredBy?.[myId]) continue;
        if (myPassive < tk.stealthDC) continue;
        passiveCandidates.push({ x: tk.x, y: tk.y });
      }
      for (const d of mapDoors || []) {
        if (!d || d.type !== 'secret' || d.secretRevealed) continue;
        if (d.discoveryDC == null) continue;
        if (myPassive < d.discoveryDC) continue;
        const a = d.pts?.[0];
        const b = d.pts?.[d.pts.length - 1];
        if (!a || !b) continue;
        passiveCandidates.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
      }
      for (const tp of Object.values(teleports || {})) {
        if (!tp.hidden) continue;
        if (tp.discoveryDC == null) continue;
        if (tp.discoveredBy?.[myId]) continue;
        if (myPassive < tp.discoveryDC) continue;
        passiveCandidates.push({ x: tp.a.x, y: tp.a.y });
        passiveCandidates.push({ x: tp.b.x, y: tp.b.y });
      }
    }

    // Trap-on-path: first untriggered trap match.
    const trapList = Object.values(traps || {});
    let firstTrapIdx = -1;
    let firstTrapHit = null;
    for (let i = 0; i < path.length; i++) {
      const cellX = path[i].x, cellY = path[i].y;
      if (firstPassiveIdx === -1 && passiveCandidates.length > 0) {
        const hit = passiveCandidates.some((h) =>
          Math.max(Math.abs(h.x - cellX), Math.abs(h.y - cellY)) <= PASSIVE_RANGE
        );
        if (hit) firstPassiveIdx = i;
      }
      if (firstTrapIdx === -1 && triggerTrap && trapList.length > 0) {
        const hit = trapList.find((t) => {
          if (t.triggered) return false;
          if (t.triggerType === 'adjacent') {
            return Math.max(Math.abs(t.x - cellX), Math.abs(t.y - cellY)) <= 1;
          }
          return t.x === cellX && t.y === cellY;
        });
        if (hit) { firstTrapIdx = i; firstTrapHit = hit; }
      }
      if (firstPassiveIdx !== -1 || firstTrapIdx !== -1) break;
    }

    let effectivePath = path;
    let pendingTrapTrigger = null;
    if (firstPassiveIdx !== -1 && (firstTrapIdx === -1 || firstPassiveIdx <= firstTrapIdx)) {
      effectivePath = path.slice(0, firstPassiveIdx + 1);
    } else if (firstTrapIdx !== -1) {
      effectivePath = path.slice(0, firstTrapIdx + 1);
      pendingTrapTrigger = firstTrapHit.id;
    }

    if (effectivePath.length === 0) {
      routeRef.current = [];
      return;
    }

    const dest = effectivePath[effectivePath.length - 1];

    // Use the live token via ref to avoid stale-closure teleport-back: if
    // a prior move's Firestore snapshot hadn't arrived by the time
    // commitDrawing was captured into the window listener, `token.x/y`
    // would be the OLD position and the walk animation would start there.
    const liveToken = tokenRef.current;

    // Animate along the drawn route.
    const waypoints = [{ x: liveToken.x, y: liveToken.y }, ...effectivePath];
    tokenAnimator.startAnimation(liveToken.id, waypoints);

    await moveToken(liveToken.id, dest.x, dest.y);

    if (pendingTrapTrigger) {
      triggerTrap(pendingTrapTrigger, liveToken.id);
    }

    // Teleport: only on final cell, only if no trap fired and not shift-bypassed.
    // When a teleport fires we treat the entry cell as a free transport — the
    // player doesn't pay movement for *standing on* the portal, only for the
    // walk that got them there.
    let teleportRefund = 0;
    if (!pendingTrapTrigger && teleports) {
      const hit = findTeleportFromCell(teleports, dest.x, dest.y);
      if (hit) {
        const destCell = hit.dest;
        tokenAnimator.startTeleport(liveToken.id, { x: dest.x, y: dest.y }, { x: destCell.x, y: destCell.y });
        setTimeout(() => moveToken(liveToken.id, destCell.x, destCell.y), 160);
        const lastRouteCell = route.find((r) => r.x === dest.x && r.y === dest.y);
        teleportRefund = lastRouteCell?.cost || 1;
      }
    }

    // Charge movement budget — only cells actually walked, minus any
    // teleport-entry refund. Use the live movementUsed via ref so the
    // budget accrues against the current value rather than a stale one.
    if (!isDm && inCombat) {
      const walkedCost = effectivePath.reduce((acc, c) => {
        const rc = route.find((r) => r.x === c.x && r.y === c.y);
        return acc + (rc?.cost || 1);
      }, 0);
      const netCost = Math.max(0, walkedCost - teleportRefund);
      updateToken(liveToken.id, { movementUsed: movementUsedRef.current + netCost });
    }

    // Fog reveal from new position (player tokens only).
    if ((liveToken.type === 'player' || liveToken.type === 'friendlyNPC') && !liveToken.mirrorDuplicate) {
      const visible = computeVisibleCells(dest.x, dest.y, size, wallSegs, cells, gridW, gridH, (liveToken.visionRange || 60) / 5);
      if (visible.length > 0) queueFogReveal(visible);
    }

    routeRef.current = [];
  }, [players, mapDoors, allTokens, traps, teleports, triggerTrap, moveToken, updateToken,
      isDm, inCombat, size, wallSegs, cells, gridW, gridH, queueFogReveal,
      onDrawingChange, onDragStateChange, auth?.playerId]);

  const drawCtxRef = useRef({});
  drawCtxRef.current = {
    cells, gridW, gridH, size,
    allEdges: wallEdges,
    wallBlockedCells,
    blockedCells, difficultCells,
    moverProne: tokenIsProne(token),
    isFlying,
  };

  const stepRouteToCell = useCallback((cell) => {
    if (!cell) return;
    const cur = routeRef.current;
    // Read live token position via ref so a long-press that started before
    // the previous move's snapshot arrived still anchors at the current
    // (post-move) cell, not the stale captured one.
    const liveToken = tokenRef.current;
    const anchor = { x: liveToken.x, y: liveToken.y };

    if (cell.x === anchor.x && cell.y === anchor.y) {
      // Back to anchor — clear route.
      if (cur.length > 0) updateDrawing([]);
      return;
    }
    const prev = cur.length === 0 ? anchor : cur[cur.length - 1];
    if (cell.x === prev.x && cell.y === prev.y) return;

    // Backtrack — trim if cell already in route.
    const idx = cur.findIndex((c) => c.x === cell.x && c.y === cell.y);
    if (idx >= 0) {
      updateDrawing(cur.slice(0, idx + 1));
      return;
    }

    const res = canAppendCell(prev, cell, drawCtxRef.current);
    if (!res.ok) return;

    const newCost = routeTotalCost(cur) + res.cost;
    if (inCombat && newCost > effectiveSpeed) return;

    updateDrawing([...cur, { x: cell.x, y: cell.y, cost: res.cost, difficult: res.difficult }]);
  }, [updateDrawing, inCombat, effectiveSpeed]);

  const startDrawing = useCallback((node, stage) => {
    isDrawingRef.current = true;
    routeRef.current = [];
    drawNodeRef.current = node;

    // Stop Konva's drag and snap the node back. We take over input tracking
    // entirely via window listeners from here.
    try { node.stopDrag(); } catch { /* node may be gone */ }
    node.x(originRef.current.x);
    node.y(originRef.current.y);
    if (typeof node.getLayer === 'function') node.getLayer()?.batchDraw();

    // Tactile cue on mobile.
    navigator.vibrate?.(30);

    // Surface the reachable-cells overlay just like a drag would.
    if (!isObject) {
      const moverProne = tokenIsProne(token);
      const { reachable } = computeReachable(
        token.x, token.y, effectiveSpeed, size,
        cells, blockedCells, gridW, gridH,
        wallEdges, wallBlockedCells,
        isFlying, difficultCells, moverProne,
      );
      let visualDifficult;
      if (isFlying) {
        visualDifficult = new Set();
      } else if (moverProne) {
        visualDifficult = new Set(reachable);
      } else {
        visualDifficult = new Set(difficultCells);
        for (const key of reachable) {
          const [x, y] = key.split(',').map(Number);
          if (cells[y * gridW + x]?.difficult) visualDifficult.add(key);
        }
      }
      onDragStateChange?.(token.id, reachable, visualDifficult);
    }

    updateDrawing([]);

    const screenToCell = (clientX, clientY) => {
      if (!stage) return null;
      const rect = stage.container().getBoundingClientRect();
      const transform = stage.getAbsoluteTransform().copy().invert();
      const p = transform.point({ x: clientX - rect.left, y: clientY - rect.top });
      return { x: Math.floor(p.x / CELL_SIZE), y: Math.floor(p.y / CELL_SIZE) };
    };

    const onMove = (ev) => {
      if (!isDrawingRef.current) return;
      const cell = screenToCell(ev.clientX, ev.clientY);
      if (cell) stepRouteToCell(cell);
    };
    const onUp = () => {
      if (!isDrawingRef.current) return;
      commitDrawing();
    };
    const onCancel = () => cancelDrawing();
    const onKey    = (ev) => { if (ev.key === 'Escape') cancelDrawing(); };

    window.addEventListener('pointermove',   onMove);
    window.addEventListener('pointerup',     onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown',       onKey);

    drawingCleanupRef.current = () => {
      window.removeEventListener('pointermove',   onMove);
      window.removeEventListener('pointerup',     onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown',       onKey);
    };
  }, [token, size, cells, blockedCells, gridW, gridH, wallEdges, wallBlockedCells,
      isFlying, difficultCells, effectiveSpeed, isObject, onDragStateChange,
      updateDrawing, stepRouteToCell, commitDrawing, cancelDrawing]);

  const handlePressStart = useCallback((stageOrEvt, node, clientX, clientY) => {
    if (!canRouteDraw) return;
    const stage = stageOrEvt;
    pressStartRef.current = { x: clientX, y: clientY };

    // Track movement during the 400 ms window; cancel the timer if the finger
    // moves more than 12 px (the user wants a normal drag).
    const onPreMove = (ev) => {
      if (!pressTimerRef.current) return;
      const dx = ev.clientX - pressStartRef.current.x;
      const dy = ev.clientY - pressStartRef.current.y;
      if (Math.hypot(dx, dy) > 12) {
        clearTimeout(pressTimerRef.current);
        pressTimerRef.current = null;
        window.removeEventListener('pointermove', onPreMove);
        window.removeEventListener('pointerup',   onPreUp);
      }
    };
    const onPreUp = () => {
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
        pressTimerRef.current = null;
      }
      window.removeEventListener('pointermove', onPreMove);
      window.removeEventListener('pointerup',   onPreUp);
    };
    window.addEventListener('pointermove', onPreMove);
    window.addEventListener('pointerup',   onPreUp);

    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      window.removeEventListener('pointermove', onPreMove);
      window.removeEventListener('pointerup',   onPreUp);
      startDrawing(node, stage);
    }, 400);
  }, [canRouteDraw, startDrawing]);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    if (drawingCleanupRef.current) drawingCleanupRef.current();
  }, []);

  // ── Concealment (invisible / burrowed / stealthed) ───────────────────────
  const isInvisible = conditions.includes('invisible') || buffs.some((b) => b.id === 'invisible');
  const isBurrowed  = conditions.includes('burrowed');
  const isStealthed = buffs.some((b) => b.id === 'stealthed') && token.stealthDC != null;
  const isConcealed = isInvisible || isBurrowed;

  // In combat, invisible tokens vanish completely for everyone except their owner
  if (isInvisible && inCombat && !isMyToken) return null;
  // Outside combat, concealed tokens are hidden from non-DM non-owners (burrowed also applies)
  if (isConcealed && !isDm && !isMyToken) return null;
  // Stealthed tokens: visible to DM and owner; visible to other players only if
  // they've personally discovered it via perception.
  if (isStealthed && !isDm && !isMyToken) {
    const viewerId = auth?.playerId;
    if (!viewerId || !token.discoveredBy?.[viewerId]) return null;
  }

  const ghostMode = isConcealed && isMyToken;

  return (
    <Group
      x={pixel.x + cellPxW / 2}
      y={pixel.y + cellPxH / 2}
      offsetX={cellPxW / 2}
      offsetY={cellPxH / 2}
      scaleX={0.95}
      scaleY={0.95}
      rotation={isObject && effW !== effH ? 0 : rotation}
      opacity={teleportOpacity}
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseDown={(e) => {
        if (!canRouteDraw) return;
        const evt = e.evt;
        // e.target is whatever child shape was clicked; e.currentTarget is
        // this Group (the draggable node). The Group is what we need to
        // stopDrag / toggle draggable on, so pass currentTarget through.
        const groupNode = e.currentTarget;
        handlePressStart(groupNode.getStage(), groupNode, evt.clientX, evt.clientY);
      }}
      onTouchStart={(e) => {
        if (!canRouteDraw) return;
        const t = e.evt.touches?.[0] || e.evt.changedTouches?.[0];
        if (!t) return;
        const groupNode = e.currentTarget;
        handlePressStart(groupNode.getStage(), groupNode, t.clientX, t.clientY);
      }}
      onClick={() => onSelect && onSelect(token.id)}
      onTap={() => onSelect && onSelect(token.id)}
    >
      {isObject ? (
        <ObjectToken token={token} cellPxW={cellPxW} cellPxH={cellPxH} hpPct={hpPct} condColour={condColour} selected={selected} isActiveToken={isActiveToken} isDm={isDm} ghostMode={ghostMode} />
      ) : token.type === 'player' ? (
        <PlayerToken token={token} cellPx={cellPx} hpPct={hpPct} condColour={condColour} ringColour={ringColour} selected={selected} isActiveToken={isActiveToken} ghostMode={ghostMode} combatAnim={combatAnim} />
      ) : (
        <CreatureToken token={token} cellPx={cellPx} hpPct={hpPct} condColour={condColour} ringColour={ringColour} selected={selected} isActiveToken={isActiveToken} isDm={isDm} ghostMode={ghostMode} packKey={packKey} />
      )}
      {ghostMode && (
        <Text
          x={cellPxW - 16} y={1}
          text={isBurrowed ? '⬇️' : '👻'}
          fontSize={12}
          listening={false}
        />
      )}
      {/* Encounter highlight — green glow (DM only) */}
      {highlighted && (
        <Rect
          x={-3} y={-3}
          width={cellPxW + 6} height={cellPxH + 6}
          stroke="#4ade80"
          strokeWidth={2}
          fill="transparent"
          cornerRadius={cellPxW === cellPxH ? cellPxW / 2 : 4}
          opacity={0.7}
          listening={false}
          shadowColor="#4ade80"
          shadowBlur={10}
          shadowEnabled
        />
      )}
      {/* Engaged indicator — red dashed ring */}
      {token.engaged && (
        <>
          <Rect
            x={-2} y={-2}
            width={cellPxW + 4} height={cellPxH + 4}
            stroke="#ef4444"
            strokeWidth={2}
            dash={[4, 3]}
            fill="transparent"
            cornerRadius={cellPxW === cellPxH ? cellPxW / 2 : 4}
            opacity={0.8}
            listening={false}
          />
          <Text
            x={0} y={-2}
            text="⚔"
            fontSize={10}
            listening={false}
          />
        </>
      )}
      {/* Subtle "real" indicator for the player only (not DM) — so they know which is theirs */}
      {!isDm && isMyToken && token.mirrorGroupId && !token.mirrorDuplicate && (
        <Circle
          x={cellPx / 2} y={2}
          radius={3}
          fill="#818cf8"
          opacity={0.7}
          listening={false}
          shadowColor="#818cf8"
          shadowBlur={6}
        />
      )}
      {/* ✨ Inspiration indicator — visible to everyone while the player
          holds inspiration. RAW it's public info. */}
      {token.ownerId && players?.[token.ownerId]?.inspiration && (
        <Text
          x={-cellPxW * 0.18}
          y={-cellPxH * 0.22}
          text="✨"
          fontSize={Math.max(14, cellPx * 0.32)}
          listening={false}
          shadowColor="#fbbf24"
          shadowBlur={8}
        />
      )}
      {/* Quick emote — pops above the token for 2s. Players see emotes from
          other players (and their own as confirmation); DM and Display view
          skip the render so the table stays focused on the DM's surfaces.
          Only mount when there's actually an emote on the token — otherwise
          we'd be rendering 6+ EmoteBubble components every animation frame
          for nothing. */}
      {token.lastEmote?.ts && !isDm && auth?.playerId && (
        <EmoteBubble emote={token.lastEmote} cellPxW={cellPxW} cellPx={cellPx} />
      )}
    </Group>
  );
}

// Small floating emoji that auto-clears 2s after its timestamp. Caller
// guarantees emote.ts is truthy (so this never wastes work for tokens
// without a live emote).
function EmoteBubble({ emote, cellPxW, cellPx }) {
  const [, forceRerender] = useReducer((x) => x + 1, 0);
  const ts = emote.ts;
  useEffect(() => {
    const remaining = (ts + 2200) - Date.now();
    if (remaining <= 0) return;
    const id = setTimeout(forceRerender, remaining);
    return () => clearTimeout(id);
  }, [ts]);
  if (!emote.emoji) return null;
  if (Date.now() - ts > 2000) return null;
  return (
    <Text
      x={cellPxW * 0.2}
      y={-cellPx * 0.55}
      text={emote.emoji}
      fontSize={Math.max(20, cellPx * 0.55)}
      listening={false}
      shadowColor="#000"
      shadowBlur={6}
      shadowOpacity={0.6}
    />
  );
}

// React.memo skips Token re-renders when prop refs haven't changed. The
// parent re-renders on every Firestore tokens snapshot AND on every camera-
// follow rAF tick (setPos in MapCanvas → MapCanvas re-renders → TokenLayer
// re-renders). Without memo, every token would reconcile 60×/sec during a
// camera follow even though nothing about them changed. Equality is shallow:
// the parent supplies stable refs for everything (memo'd cells, hoisted
// blockSets per-id, callback identities).
const Token = React.memo(TokenInner);

// ── TokenLayer export ──────────────────────────────────────────────────────
export default function TokenLayer({ isDm, onTokenSelect, selectedTokenId, hoveredTokenId = null, excludeId = null, onlyId = null, readOnly = false, highlightTokenIds = null }) {
  const { tokens, initiative } = useCampaign();
  const { cells, gridW, gridH, walls, doors, queueFogReveal } = useMap();
  const activeTokenId = initiative.turnOrder[initiative.activeIndex] ?? null;
  const typeOrder     = { player: 0, friendlyNPC: 1, summon: 2, monster: 3, object: 4 };

  // Compute wall data once per walls change — shared across all Token children
  const wallEdges        = useMemo(() => buildWallEdges(walls),        [walls]);
  const wallSegs         = useMemo(() => buildWallSegs(walls),         [walls]);
  const wallBlockedCells = useMemo(() => buildWallBlockedCells(walls), [walls]);

  // Door edges/blocked — only non-open doors block movement
  const doorEdges        = useMemo(() => buildDoorEdges(doors),        [doors]);
  const doorBlockedCells = useMemo(() => buildDoorBlockedCells(doors), [doors]);

  // Merge wall + door edges for movement
  const allEdges = useMemo(() => {
    if (doorEdges.size === 0) return wallEdges;
    const merged = new Set(wallEdges);
    for (const e of doorEdges) merged.add(e);
    return merged;
  }, [wallEdges, doorEdges]);

  const allBlockedCells = useMemo(() => {
    if (doorBlockedCells.size === 0) return wallBlockedCells;
    const merged = new Set(wallBlockedCells);
    for (const c of doorBlockedCells) merged.add(c);
    return merged;
  }, [wallBlockedCells, doorBlockedCells]);

  // Merge wall + door segs for vision. Always include doors (pass false for isDm)
  // because these segs drive fog REVEAL for player tokens — doors must block even
  // when the DM is the one moving the token, otherwise fog permanently leaks past
  // closed doors.
  const doorVisionSegs = useMemo(() => buildDoorVisionSegs(doors, false), [doors]);
  const allVisionSegs  = useMemo(() => {
    if (doorVisionSegs.length === 0) return wallSegs;
    return [...wallSegs, ...doorVisionSegs];
  }, [wallSegs, doorVisionSegs]);

  const [draggingId,      setDraggingId]      = useState(null);
  const [reachableCells,  setReachableCells]  = useState(null);
  const [reachDifficult,  setReachDifficult]  = useState(null);
  const [currentDrawing,  setCurrentDrawing]  = useState(null);  // route-mode visualisation

  // ── Animation kickoff on token position change ───────────────────────────
  // Diffs the tokens map between renders; for any token whose x/y changed,
  // compute a walkable path and start a waypoint animation. Skips long
  // teleports, cross-map jumps, objects out of combat, and positions already
  // targeted by an ongoing animation (self-drag smooth-snap).
  //
  // Must run as a layout effect (pre-paint): if we ran after paint, player/display
  // views would paint once at the committed destination before the animation
  // starts, causing a visible flash. Running pre-paint + forcing a synchronous
  // re-render lets Token read the in-flight animation's t≈0 (origin) position.
  const prevTokensRef = useRef(null);
  const inCombat      = initiative.phase === 'active';
  const [, forceRerender] = useReducer((x) => x + 1, 0);
  useLayoutEffect(() => {
    const nextMap = new Map();
    for (const id in tokens) nextMap.set(id, tokens[id]);
    const prev = prevTokensRef.current;
    prevTokensRef.current = nextMap;
    if (!prev) return; // first render — don't animate initial load

    let startedAny = false;
    for (const [id, nextTok] of nextMap) {
      const prevTok = prev.get(id);
      if (!prevTok) continue;
      if (prevTok.x === nextTok.x && prevTok.y === nextTok.y) continue;
      if ((prevTok.mapId || null) !== (nextTok.mapId || null)) continue;
      if (nextTok.type === 'object' && !inCombat) continue;

      const dx = nextTok.x - prevTok.x;
      const dy = nextTok.y - prevTok.y;
      if (Math.hypot(dx, dy) > 10) continue; // long jump → snap

      // Skip if an in-flight animation already ends at the new cell
      // (self-drag smooth-snap kicked its own waypoints).
      const existing = tokenAnimator.getAnimation(id);
      if (existing) {
        const last = existing.waypoints[existing.waypoints.length - 1];
        if (last.x === nextTok.x && last.y === nextTok.y) continue;
      }
      // A teleport flash is in progress — don't kick a walk path. The flash
      // controls position visually (vanish at A, reappear at B).
      if (tokenAnimator.isTeleporting(id)) continue;

      const isFlying   = (nextTok.buffs || []).some((b) => b.id === 'fly');
      const size       = nextTok.size || 1;
      const moverProne = tokenIsProne(nextTok);

      // Build blocked + difficult sets using 5e creature-space rules.
      const blocked   = new Set();
      const difficult = new Set();
      for (const other of nextMap.values()) {
        if (other.id === id || other.defeated) continue;
        if (other.type === 'object') {
          const oFoot = objectFootprint(other);
          for (let dx = 0; dx < oFoot.w; dx++)
            for (let dy = 0; dy < oFoot.h; dy++)
              blocked.add(`${other.x + dx},${other.y + dy}`);
          continue;
        }
        const rule = passRule(nextTok, other);
        if (rule === 'free') continue;
        const s = other.size || 1;
        const target = rule === 'block' ? blocked : difficult;
        for (let dx = 0; dx < s; dx++)
          for (let dy = 0; dy < s; dy++)
            target.add(`${other.x + dx},${other.y + dy}`);
      }

      const { path } = findPath(
        prevTok.x, prevTok.y, nextTok.x, nextTok.y,
        size, cells, blocked, gridW, gridH, allEdges, allBlockedCells,
        isFlying, difficult, moverProne,
      );

      const waypoints = path && path.length > 0
        ? [{ x: prevTok.x, y: prevTok.y }, ...path]
        : [{ x: prevTok.x, y: prevTok.y }, { x: nextTok.x, y: nextTok.y }];
      tokenAnimator.startAnimation(id, waypoints);
      startedAny = true;
    }

    // If any animation was started, force a synchronous re-render so Tokens
    // repaint at the animation's starting (origin) position before the browser
    // paints — otherwise the first paint shows the committed destination.
    // flushSync guarantees React commits the re-render (and react-konva flushes
    // to the Konva stage) before returning control to the browser.
    if (startedAny) flushSync(() => forceRerender());
  }, [tokens, inCombat, cells, gridW, gridH, allEdges, allBlockedCells]);

  const handleDragStateChange = useCallback((tokenId, reachable, difficult = null) => {
    setDraggingId(tokenId);
    setReachableCells(reachable);
    setReachDifficult(difficult);
  }, []);

  const allTokens = useMemo(() => {
    return Object.values(tokens).sort((a, b) => {
      if (a.defeated !== b.defeated) return a.defeated ? 1 : -1;
      return (typeOrder[a.type] ?? 3) - (typeOrder[b.type] ?? 3);
    });
  }, [tokens]);

  // Object cells per token id — used only by the hoisted blockSetsByToken
  // below to recognise each object's full footprint as a hard block.
  const objectCellsById = useMemo(() => {
    const objById = new Map();
    for (const t of allTokens) {
      if (t.defeated || t.type !== 'object') continue;
      const f = objectFootprint(t);
      const cellsList = [];
      for (let dx = 0; dx < f.w; dx++)
        for (let dy = 0; dy < f.h; dy++)
          cellsList.push(`${t.x + dx},${t.y + dy}`);
      objById.set(t.id, cellsList);
    }
    return objById;
  }, [allTokens]);

  // Hoisted blockedCells/difficultCells per token. Computing once at the parent
  // avoids re-running the same O(N²) sweep inside every Token's useMemo when
  // any other token changes. Each Token gets a stable {blocked,difficult} slice;
  // re-renders only fire when its own slice's reference identity changes.
  const blockSetsByToken = useMemo(() => {
    const result = new Map();
    for (const moving of allTokens) {
      const blocked   = new Set();
      const difficult = new Set();
      for (const other of allTokens) {
        if (other.id === moving.id || other.defeated) continue;
        if (other.type === 'object') {
          const oc = objectCellsById.get(other.id);
          if (oc) for (const c of oc) blocked.add(c);
          continue;
        }
        const rule = passRule(moving, other);
        if (rule === 'free') continue;
        const s = other.size || 1;
        const target = rule === 'block' ? blocked : difficult;
        for (let dx = 0; dx < s; dx++)
          for (let dy = 0; dy < s; dy++)
            target.add(`${other.x + dx},${other.y + dy}`);
      }
      result.set(moving.id, { blocked, difficult });
    }
    return result;
  }, [allTokens, objectCellsById]);

  let sorted = [...allTokens];
  // Hide tokens outside the grid from non-DM views (DM stages tokens off-grid)
  if (!isDm) sorted = sorted.filter((t) => {
    const s = t.size || 1;
    return t.x >= 0 && t.y >= 0 && t.x + s <= gridW && t.y + s <= gridH;
  });
  if (excludeId) sorted = sorted.filter((t) => t.id !== excludeId);
  if (onlyId)    sorted = sorted.filter((t) => t.id === onlyId);

  return (
    <>
      <MoveRangeLayer reachableCells={reachableCells} difficultCells={reachDifficult} />
      <Layer>
        {sorted.map((token) => (
          <Token
            key={token.id}
            token={token}
            isDm={isDm}
            readOnly={readOnly}
            isActiveToken={token.id === activeTokenId || token.id === hoveredTokenId}
            selected={token.id === selectedTokenId}
            highlighted={isDm && highlightTokenIds ? highlightTokenIds.has(token.id) : false}
            onSelect={onTokenSelect}
            allTokens={allTokens}
            cells={cells}
            gridW={gridW}
            gridH={gridH}
            wallEdges={allEdges}
            wallSegs={allVisionSegs}
            wallBlockedCells={allBlockedCells}
            queueFogReveal={queueFogReveal}
            blockSets={blockSetsByToken.get(token.id)}
            onDragStateChange={handleDragStateChange}
            onDrawingChange={setCurrentDrawing}
          />
        ))}
        <RouteOverlay drawing={currentDrawing} />
      </Layer>
    </>
  );
}
